"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { HStack, Text, VStack, useFilter } from "@chakra-ui/react";
import usePageSearch from "../hooks/usePageSearch";
import useSearchCriteriaActions from "../hooks/useSearchCriteriaActions";
import useSearchCriteriaSelection from "../hooks/useSearchCriteriaSelection";
import { useUserChoices } from "@/state/globalStore";
import { Criterion } from "@/generated/fastapi";
import LoadingState from "@/shared/ui/LoadingState";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import { useSystemDesign } from "@/shared/system-design";
import { PROFILE_SHAPING_LABELS } from "@/features/profile-shaping";
import SearchCriteriaMenu from "./SearchCriteriaMenu";
import SearchFullContentChip from "./SearchFullContentChip";
import SearchCriteriaList from "./SearchCriteriaList";

const FULL_CONTENT_CHIP_ID = "__FULL_CONTENT__";

// PARKED FEATURE:
// The chip-based search-settings workflow is intentionally kept in code but is
// currently unreachable from the navbar. Keep this logic intact for possible
// future reactivation instead of deleting it.
export default function QueryChips() {
  const { pageSearch } = usePageSearch();
  const { targetsToRerank, activePrototypeCategory } = useUserChoices();
  const { isMobileDevice } = useSystemDesign();

  const [showAll, setShowAll] = useState(false);
  const [predictiveFiltering, setPredictiveFiltering] = useState(false);

  const { contains } = useFilter({ sensitivity: "base" });

  const targetId =
    targetsToRerank && targetsToRerank.length > 0
      ? targetsToRerank[0].id
      : undefined;

  const {
    clearCriteria,
    selectedCriterionIds,
    selectedCriteriaCount,
    toggleCriterion,
    isCriterionSelected,
  } = useSearchCriteriaSelection();

  const { criteria, deleteCriteria, extractCriteria } =
    useSearchCriteriaActions({
      targetId,
      prototypeCategory: activePrototypeCategory,
    });

  const fullContentChip: Criterion | null = useMemo(() => {
    if (!targetId || !targetsToRerank || targetsToRerank.length === 0) {
      return null;
    }

    const target = targetsToRerank[0];
    return {
      id: FULL_CONTENT_CHIP_ID,
      mindstring: target.content || "",
      target_id: targetId,
      // Other fields can be undefined/null for synthetic chip
    } as Criterion;
  }, [targetId, targetsToRerank]);

  const autoSelectedTargetRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!fullContentChip || !targetId) {
      autoSelectedTargetRef.current = undefined;
      return;
    }

    if (autoSelectedTargetRef.current === targetId) return;

    const isFullContentSelected = isCriterionSelected(FULL_CONTENT_CHIP_ID);

    if (!isFullContentSelected) {
      toggleCriterion(fullContentChip);
      autoSelectedTargetRef.current = targetId;
    } else {
      autoSelectedTargetRef.current = targetId;
    }
  }, [fullContentChip, isCriterionSelected, targetId, toggleCriterion]);

  const filteredCriteria = useMemo(() => {
    if (!criteria.data || criteria.data.length === 0) return [];

    if (!predictiveFiltering) return criteria.data;

    const searchQuery = pageSearch.searchQuery?.trim();
    if (!searchQuery) return criteria.data;

    return criteria.data.filter((criterion) =>
      contains(criterion.mindstring || "", searchQuery),
    );
  }, [criteria.data, pageSearch.searchQuery, contains, predictiveFiltering]);

  const HIGHLIGHT_COLOR = "yellow.emphasized";

  if (!targetId) {
    return (
      <GenericEmptyComponent
        title={PROFILE_SHAPING_LABELS.chooseFocus}
        message="Choose a focus lens to get reader-relevance suggestions."
        variant={isMobileDevice ? "inline" : "normal"}
      />
    );
  }

  if (criteria.isLoading && (!criteria.data || criteria.data.length === 0)) {
    return (
      <LoadingState
        message="Loading suggestions…"
        variant={isMobileDevice ? "inline" : "normal"}
      />
    );
  }

  if (criteria.isExtracting) {
    return (
      <LoadingState
        message="Preparing suggestions…"
        variant={isMobileDevice ? "inline" : "normal"}
      />
    );
  }

  if (criteria.error) {
    return (
      <GenericEmptyComponent
        title="Error loading suggestions."
        message={criteria.error}
        variant={isMobileDevice ? "inline" : "normal"}
      />
    );
  }

  const regularCriteriaCount =
    selectedCriteriaCount - (isCriterionSelected(FULL_CONTENT_CHIP_ID) ? 1 : 0);

  return (
    <VStack align="stretch">
      <HStack justify="space-between">
        <Text>
          {!Boolean(fullContentChip)
            ? "Choose a focus to search"
            : regularCriteriaCount === 0
              ? "Searching full focus content"
              : `${regularCriteriaCount} criteria selected for search`}
        </Text>
        <SearchCriteriaMenu
          isBusy={criteria.isLoading || criteria.isExtracting}
          isMobileDevice={isMobileDevice}
          predictiveFiltering={predictiveFiltering}
          regularCriteriaCount={regularCriteriaCount}
          canDeleteSelected={selectedCriterionIds.length > 0}
          onExtract={async () => {
            if (!targetId) return;
            await extractCriteria({
              target_id: targetId,
              prototypes: activePrototypeCategory
                ? [activePrototypeCategory]
                : undefined,
              should_ingest_criteria: true,
            });
          }}
          onTogglePredictiveFiltering={() =>
            setPredictiveFiltering((prev) => !prev)
          }
          onClearSelected={() => clearCriteria()}
          onDeleteSelected={async () => {
            if (selectedCriterionIds.length === 0) return;
            const result = await deleteCriteria({
              criterion_ids: selectedCriterionIds,
              delete_mode: "permanent",
            });
            if (result) clearCriteria();
          }}
        />
      </HStack>

      <VStack align="stretch">
        {fullContentChip && (
          <SearchFullContentChip
            chip={fullContentChip}
            contentText={
              targetsToRerank?.[0]?.content || "Focus content not available"
            }
            isMobileDevice={isMobileDevice}
            isSelected={isCriterionSelected(fullContentChip.id)}
            onToggle={() => toggleCriterion(fullContentChip)}
          />
        )}

        <SearchCriteriaList
          criteria={filteredCriteria}
          isMobileDevice={isMobileDevice}
          predictiveFiltering={predictiveFiltering}
          searchQuery={pageSearch.searchQuery}
          highlightColor={HIGHLIGHT_COLOR}
          showAll={showAll}
          onToggleShowAll={() => setShowAll((state) => !state)}
          isCriterionSelected={isCriterionSelected}
          onToggleCriterion={toggleCriterion}
          onDeleteCriterion={async (criterion: Criterion) => {
            const result = await deleteCriteria({
              criterion_ids: [criterion.id],
              delete_mode: "permanent",
            });
            if (result) clearCriteria([criterion]);
          }}
        />
      </VStack>
    </VStack>
  );
}
